/**
 * @file OverviewCards.tsx
 * @brief The six headline cards of Super Admin → Analytics (W22), from B25
 *        `overview`: MRR, ARR, paying accounts, Free users, conversion,
 *        gross margin %.
 *
 * @details
 * A null from the API is "–" with its reason, never 0. MRR / ARR are the
 * live state (not the period) and VAT-inclusive as charged; the hints say so.
 *
 * @author DES (prepared with Claude)
 * @date 25-10-2026
 */

import { BadgePercent, CalendarClock, CreditCard, Gift, PiggyBank, Repeat } from "lucide-react";
import { StatCard } from "@/features/analytics/components/StatCard";
import { GridCell, GridCells } from "@/features/analytics/components/GridBlock";
import {
  formatCount,
  formatMoney,
  formatPct,
  notConvertedLines,
  NULL_REASONS,
  pick,
} from "../businessMath";
import type { Currency, Overview } from "../types";
import { MoneyText, NullValue } from "./common";

export function OverviewCards({
  data,
  isLoading,
  currency,
}: {
  data: Overview | undefined;
  isLoading: boolean;
  currency: Currency;
}) {
  const d = data;
  const mrrReason = d?.mrr_reason || NULL_REASONS.mrr_default;
  const otherMrr = notConvertedLines(d?.mrr);
  const marginPct = d?.gross_margin_pct ?? null;

  return (
    <GridCells className="grid-cols-1 sm:grid-cols-2 xl:grid-cols-3">
      <GridCell padded={false}>
        <StatCard
          variant="cell"
          label="MRR"
          icon={Repeat}
          isLoading={isLoading}
          value={d && d.mrr ? <MoneyText minor={pick(d.mrr, currency)} currency={currency} /> : <NullValue reason={mrrReason} />}
          hint={
            otherMrr.length
              ? `Live now, as charged (VAT incl.) · plus ${otherMrr.join(", ")} not converted`
              : "Live now, as charged (VAT incl.)"
          }
        />
      </GridCell>
      <GridCell padded={false}>
        <StatCard
          variant="cell"
          label="ARR"
          icon={CalendarClock}
          isLoading={isLoading}
          value={d && d.arr ? <MoneyText minor={pick(d.arr, currency)} currency={currency} /> : <NullValue reason={mrrReason} />}
          hint="MRR × 12"
        />
      </GridCell>
      <GridCell padded={false}>
        <StatCard
          variant="cell"
          label="Active paid accounts"
          icon={CreditCard}
          isLoading={isLoading}
          value={formatCount(d?.paying_accounts)}
          hint={d ? `${formatCount(d.paying_seats)} seats · a team plan is one account` : undefined}
        />
      </GridCell>
      <GridCell padded={false}>
        <StatCard
          variant="cell"
          label="Free users"
          icon={Gift}
          isLoading={isLoading}
          value={formatCount(d?.free_users.total)}
          hint={
            d
              ? `${formatCount(d.free_users.active)} active · ${formatCount(d.free_users.used_up)} used up · ${formatCount(d.free_users.expired)} expired`
              : undefined
          }
        />
      </GridCell>
      <GridCell padded={false}>
        <StatCard
          variant="cell"
          label="Conversion, 30 days"
          icon={BadgePercent}
          isLoading={isLoading}
          value={
            d && d.conversion_30d_pct == null ? <NullValue reason={NULL_REASONS.conversion} /> : formatPct(d?.conversion_30d_pct)
          }
          hint={d ? `7 days: ${formatPct(d.conversion_7d_pct)} · self sign-ups only · ${formatCount(d.signups)} sign-ups` : undefined}
        />
      </GridCell>
      <GridCell padded={false}>
        <StatCard
          variant="cell"
          label="Gross margin"
          icon={PiggyBank}
          isLoading={isLoading}
          value={
            d && marginPct == null ? (
              <NullValue reason={NULL_REASONS.gross_margin_pct} />
            ) : (
              <span className={marginPct != null && marginPct < 0 ? "text-destructive" : undefined}>{formatPct(marginPct)}</span>
            )
          }
          hint={
            d
              ? `${formatMoney(pick(d.gross_margin, currency), currency)} on ${formatMoney(pick(d.revenue, currency), currency)} revenue`
              : undefined
          }
        />
      </GridCell>
    </GridCells>
  );
}
